/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';

/**
 * Bookmark state for a single target with optimistic toggle + rollback.
 * When `initialSaved` is unknown the hook performs one GET /bookmarks/check.
 */
export const useBookmark = ({ targetType, targetId, initialSaved, onChange }) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(!!initialSaved);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(initialSaved !== undefined);

  useEffect(() => {
    if (checked || !targetId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/bookmarks/check', { params: { target_type: targetType, ids: targetId } });
        if (!cancelled) setSaved(!!r.data[targetId]);
      } catch { /* ignore */ }
      finally { if (!cancelled) setChecked(true); }
    })();
    return () => { cancelled = true; };
  }, [targetType, targetId, checked]);

  const toggle = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (loading) return;
    setLoading(true);
    const next = !saved;
    setSaved(next); // optimistic
    try {
      if (next) await api.post('/bookmarks', { target_type: targetType, target_id: targetId });
      else await api.delete(`/bookmarks/${targetType}/${targetId}`);
      toast.success(t(next ? 'savedAdded' : 'savedRemoved'));
      onChange?.(next);
    } catch {
      setSaved(!next); // rollback
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return { saved, loading, toggle, t };
};
